import React, { useState, useEffect, useRef } from 'react';
import { mediaAPI, MediaFile } from '../services/api';
import Icon from './Icon';

interface MediaUploaderProps {
  /** Restrict picker + library to this media type. */
  accept: 'image' | 'video';
  /** Called with the public URL (and metadata) once a file is chosen/uploaded. */
  onSelect: (file: MediaFile) => void;
}

/**
 * Reusable upload + media-library widget.
 * Uploads a file to the backend (which returns a public URL) and also lets the
 * user pick from previously uploaded media of the same type.
 */
const MediaUploader: React.FC<MediaUploaderProps> = ({ accept, onSelect }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [library, setLibrary] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showLibrary, setShowLibrary] = useState(false);

  const acceptAttr = accept === 'image' ? 'image/*' : 'video/mp4,video/quicktime,video/webm';

  const loadLibrary = async () => {
    try {
      const res = await mediaAPI.list();
      if (res.data.success) {
        setLibrary(res.data.data.files.filter((f) => f.type === accept));
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accept]);

  const handleFile = async (file: File) => {
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const res = await mediaAPI.upload(file, setProgress);
      if (res.data.success) {
        onSelect(res.data.data);
        loadLibrary();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (file: MediaFile, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${file.filename}?`)) return;
    try {
      await mediaAPI.remove(file.filename);
      loadLibrary();
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && e.target.files[0] && handleFile(e.target.files[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{ padding: '8px 14px', backgroundColor: uploading ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: 5, cursor: uploading ? 'not-allowed' : 'pointer' }}
        >
          {uploading ? `Uploading ${progress}%…` : <><Icon name="upload" size={16} /> Upload {accept}</>}
        </button>
        <button
          type="button"
          onClick={() => setShowLibrary(!showLibrary)}
          style={{ padding: '8px 14px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer' }}
        >
          <Icon name="lists" size={16} /> Library ({library.length})
        </button>
      </div>

      {uploading && (
        <div style={{ marginTop: 8, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: '#16a34a' }} />
        </div>
      )}

      {error && <div style={{ marginTop: 8, color: '#c00', fontSize: 13 }}>{error}</div>}

      {showLibrary && (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 6, backgroundColor: '#fafafa' }}>
          {library.length === 0 ? (
            <div style={{ fontSize: 13, color: '#6b7280' }}>No uploaded {accept}s yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10 }}>
              {library.map((f) => (
                <div
                  key={f.filename}
                  onClick={() => { onSelect(f); setShowLibrary(false); }}
                  title={f.filename}
                  style={{ position: 'relative', cursor: 'pointer', border: '1px solid #ddd', borderRadius: 5, overflow: 'hidden', background: '#fff' }}
                >
                  {f.type === 'image' ? (
                    <img src={f.url} alt={f.filename} style={{ width: '100%', height: 70, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}><Icon name="play" size={40} /></div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleDelete(f, e)}
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(220,38,38,0.9)', color: 'white', border: 'none', borderRadius: 3, width: 20, height: 20, cursor: 'pointer', fontSize: 12, lineHeight: '18px' }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
